'use client'

import Cta from '@/components/ui/cta'
import NavMenu from './NavMenu'
import HamburgerIcon from '@/components/svg/icon/Hamburger'
import clsx from 'clsx'
import NavItemDropdown from './NavigationItemDropdown'
import { useIsResizing } from '@/hooks/useIsResizing'
import Icon from '@/components/svg/icon'
import type { ProductFragment } from '@/lib/types/datocms/graphql/generated'

export default function NavContainer({
	nav,
	isMegaMenuOpen,
	setIsMegaMenuOpen,
	activeMegaMenu,
	setActiveMegaMenu,
	currentDropdown,
	setCurrentDropdown,
	isMobileMenuOpen,
	setIsMobileMenuOpen,
	products,
}: Readonly<{
	nav: any
	isMegaMenuOpen: boolean
	setIsMegaMenuOpen: (isMegaMenuOpen: boolean) => void
	activeMegaMenu: string | null | undefined
	setActiveMegaMenu: (activeMegaMenu: string | null | undefined) => void
	currentDropdown: string | null
	setCurrentDropdown: (currentDropdown: string | null) => void
	isMobileMenuOpen: boolean
	setIsMobileMenuOpen: (isMobileMenuOpen: boolean) => void
	products: ProductFragment[]
}>) {
	const isResizing = useIsResizing()

	const handleToggleMobileNav = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen)
	}

	return (
		<>
			<button
				className="flex items-center justify-center bg-blue w-12 h-12 rounded-lg lg:hidden"
				onClick={handleToggleMobileNav}
				aria-label="Toggle mobile nav"
			>
				<HamburgerIcon isMobileMenuOpen={isMobileMenuOpen} />
			</button>

			{/* Top Bar Nav Items / Mobile Menu */}
			<div
				className={clsx(
					'flex',
					'max-lg:flex-col max-lg:text-left max-lg:fixed max-lg:inset-0 max-lg:top-[--nav-height] max-lg:bg-white max-lg:-z-10',
					!isResizing && 'max-lg:transition-transform max-lg:duration-500 max-lg:ease-m',
					isMobileMenuOpen ? 'max-lg:translate-y-0' : 'max-lg:-translate-y-full'
				)}
			>
				{nav?.menuItems
					? nav.menuItems.map((navItem: any) => {
							if (navItem.__typename === 'NavigationItemDropdownRecord') {
								return (
									<NavItemDropdown
										key={navItem.id as string}
										dropdownOptions={navItem.navigationDropdownOptions}
										title={navItem.title}
										setShowMobileMenu={setIsMobileMenuOpen}
										setCurrentDropdown={setCurrentDropdown}
										currentDropdown={currentDropdown}
										setActiveMegaMenu={setActiveMegaMenu}
										dropdownNavigation={navItem.dropdownNavigation}
										setIsMegaMenuOpen={setIsMegaMenuOpen}
									/>
								)
							}
							if (navItem.__typename === 'NavigationItemRecord') {
								return (
									<Cta
										key={navItem.id as string}
										cta={navItem}
										className="px-1 max-lg:px-3 max-lg:py-5 max-lg:font-semibold max-lg:border-b max-lg:hover:bg-white-frosted hover:text-blue-dark transition-colors duration-300 ease-in-out"
										onClick={() => {
											setIsMobileMenuOpen(false)
										}}
										closeMegaMenu
									>
										<div className="flex items-center justify-between gap-2">
											{navItem.label}
											<Icon icon="Chevron" className="w-4 h-4 text-blue lg:hidden" />
										</div>
									</Cta>
								)
							}
							if (navItem.__typename === 'NavMenuRecord') {
								return (
									<NavMenu
										key={navItem.id as string}
										{...navItem}
										isMegaMenuOpen={isMegaMenuOpen}
										setIsMegaMenuOpen={setIsMegaMenuOpen}
										setShowMobileMenu={setIsMobileMenuOpen}
										setActiveMegaMenu={setActiveMegaMenu}
										activeMegaMenu={activeMegaMenu}
										setCurrentDropdown={setCurrentDropdown}
										products={products}
									/>
								)
							}
						})
					: null}
			</div>
		</>
	)
}
